import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDocumentVersions, useDocumentVersion, useRestoreDocumentVersion } from "../hooks/useDocuments";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { versionLabel } from "@/lib/documentVersionLabel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { KiiConfirmDialog } from "@/components/kii/kii-confirm-dialog";
import { Clock, Eye, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";

interface DocumentHistoryProps {
  projectId: string;
  documentId: string;
}

export function DocumentHistory({ projectId, documentId }: DocumentHistoryProps) {
  const { t } = useTranslation();
  const { data: versions, isLoading } = useDocumentVersions(projectId, documentId);
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewingVersionId, setViewingVersionId] = useState<string | null>(null);
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(null);
  const { formatDate } = useDateFormatter();
  const restoreMutation = useRestoreDocumentVersion();

  // Fetch detail when viewing
  const { data: versionDetail, isLoading: detailLoading } = useDocumentVersion(
    projectId,
    documentId,
    viewingVersionId ?? undefined
  );

  if (isLoading || !versions || versions.length === 0) {
    return null; // Don't show section if no versions
  }

  const handleRestore = async () => {
    if (!restoringVersionId) return;
    try {
      await restoreMutation.mutateAsync({
        projectId,
        documentId,
        versionId: restoringVersionId,
      });
      setRestoringVersionId(null);
    } catch (error) {
      console.error("Failed to restore version:", error);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Collapsible header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-accent/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Clock className="h-4 w-4 text-muted-foreground" />
          {t("documentHistory.title", { count: versions.length })}
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {restoreMutation.isError && (
        <p role="alert" className="px-4 pb-2 text-sm text-destructive">
          {t("documentDetail.restoreVersionError")}
        </p>
      )}

      {/* Version list */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-2">
          {versions.map((version) => (
            <div
              key={version.id}
              className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50 transition-colors text-sm"
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-foreground">
                  {versionLabel(version.label, t)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatDate(version.created_at, "long-datetime")}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setViewingVersionId(version.id)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
                  title={t("documentHistory.viewHint")}
                >
                  <Eye className="h-3.5 w-3.5" />
                  {t("documentHistory.view")}
                </button>
                <button
                  onClick={() => setRestoringVersionId(version.id)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
                  title={t("documentHistory.restoreHint")}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {t("documentHistory.restore")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Version detail dialog */}
      <Dialog open={!!viewingVersionId} onOpenChange={(open) => !open && setViewingVersionId(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {versionLabel(versionDetail?.label, t)} — {versionDetail?.created_at ? formatDate(versionDetail.created_at, "long-datetime") : ""}
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="h-48 bg-muted rounded animate-pulse" />
          ) : versionDetail?.content_json ? (
            <pre className="text-xs font-mono bg-muted p-4 rounded-lg overflow-x-auto max-h-[60vh] whitespace-pre-wrap">
              {JSON.stringify(versionDetail.content_json, null, 2)}
            </pre>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {t("documentHistory.noContent")}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Restore confirmation */}
      <KiiConfirmDialog
        open={!!restoringVersionId}
        onOpenChange={(open) => !open && setRestoringVersionId(null)}
        title={t("documentHistory.restoreTitle")}
        description={t("documentHistory.restoreDescription")}
        confirmText={restoreMutation.isPending ? t("documentHistory.restoring") : t("documentHistory.restore")}
        cancelText={t("documentDetail.cancel")}
        onConfirm={handleRestore}
      />
    </div>
  );
}
